import React, {createContext, useContext, useState, useEffect, ReactNode, useMemo} from 'react'
import {useAuth} from '../../../../modules/auth'
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  updateDoc,
  where,
  serverTimestamp,
  addDoc,
  getDocs,
} from 'firebase/firestore'
import db, {storage} from '../../../../../services/firebase'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import {deleteObject, ref} from 'firebase/storage'
import {format, parseISO} from 'date-fns'
import useAuditContext from 'app/Hooks/useAuditContext'
import {getReviewStatus} from '../../utils/reviewStatusUtils'
import ConfirmationModal from 'app/components/ConfirmationModal'
import {evaluateTest} from '../../../../Integrations/services/integrationTestsService'

interface TestContextProps {
  // Test data
  tests: any[]
  selectedTest: any[]
  policies: any[]
  updatePolicy: any[]
  personnels: any[]
  activeFrameworks: any[]
  policyID: string

  // File handling
  fileData: File[]
  showUploadFile: boolean
  isDropdownVisible: boolean
  selectedFile: any
  isFileDeleteModalOpen: boolean
  isDeleting: boolean

  // Notes
  noteInput: string
  editingNoteIndex: number | null
  editedNoteContent: string
  isNoteDeleteModalOpen: boolean
  selectedNoteIndex: number | null
  noteSaving: boolean
  isAdding: boolean
  setIsFileDeleteModalOpen: (isOpen: boolean) => void
  setIsNoteDeleteModalOpen: (isOpen: boolean) => void

  // Flag modal
  isFlagModalOpen: boolean
  isFlagging: boolean
  
  // Mark as not required modal
  isMarkAsNotRequiredModalOpen: boolean
  isMarkingAsNotRequired: boolean
  
  // Mark as resolved modal
  isMarkAsResolvedModalOpen: boolean
  isMarkingAsResolved: boolean
  
  // Tasks
  tasks: any[]
  isModalOpen: boolean
  isTestLoading: boolean

  // UI state
  activeTab: string
  newOwner: string
  timeframe: string

  // Current user and ID
  currentUser: any
  id: string

  // Functions
  getTests: () => void
  getPolicy: () => void
  getActivities: () => Promise<void>
  getActiveFrameworks: () => void

  // File handlers
  handleFileUpload: (files: File[] | null) => void
  handleViewClick: (file: any) => void
  handleDeleteClick: (file: any) => void
  handleConfirmFileDelete: () => Promise<void>
  handleAddFileClick: () => void
  handleDownloadClick2: (file: any) => void
  toggleDropdown: () => void

  // Note handlers
  handleNotes: (event: React.FormEvent) => Promise<void>
  setNoteInput: (value: string) => void
  handleEditNote: (index: number, content: string) => void
  handleSaveEditedNote: () => Promise<void>
  handleDeleteNote: (index: number) => void
  handleConfirmNoteDelete: () => Promise<void>
  setEditingNoteIndex: (index: number | null) => void
  setEditedNoteContent: (content: string) => void

  // Flag handlers
  handleOpenFlagModal: () => void
  handleCloseFlagModal: () => void
  handleConfirmFlag: (reason: string) => Promise<void>
  handleAccept: () => Promise<void>
  handleReEnableEvidence: () => Promise<void>

  // Mark as not required handlers
  handleOpenMarkAsNotRequiredModal: () => void
  handleCloseMarkAsNotRequiredModal: () => void
  handleConfirmMarkAsNotRequired: (reason: string) => Promise<void>

  // Mark as resolved handlers
  handleOpenMarkAsResolvedModal: () => void
  handleCloseMarkAsResolvedModal: () => void
  handleConfirmMarkAsResolved: () => Promise<void>

  // Task handlers
  handleOpenModal: () => void
  handleCloseModal: () => void
  handleRemoveTask: (taskId: string) => void
  handleConfirmTaskRemove: () => Promise<void>
  handleMarkAsComplete: (taskId: string, taskName: string) => Promise<void>
  handleMarkAsIncomplete: (taskId: string, taskName: string) => Promise<void>
  setReminder: (taskId: string, status: boolean) => Promise<void>

  // Task removal modal state
  isTaskRemoveModalOpen: boolean
  isRemovingTask: boolean
  setIsTaskRemoveModalOpen: (isOpen: boolean) => void

  // Other handlers
  handleOwner: (event: React.ChangeEvent<HTMLSelectElement>) => Promise<void>
  handleTimeframe: (event: React.ChangeEvent<HTMLSelectElement>) => Promise<void>
  handleTabClick: (tab: string) => void

  // Utility functions
  bytesToKB: (bytes: number) => string
  formatFileDate: (dateString: string) => string
  getReviewStatus: (auditorReview: any) => string | null

  // Audit context
  currentAuditContext: any

  // Audits
  audits: any[]

  // Document viewer modal
  isDocumentViewerOpen: boolean
  selectedDocument: any
  setIsDocumentViewerOpen: (isOpen: boolean) => void
  setSelectedDocument: (file: any) => void

  // Test refresh
  isRefreshingTest: boolean
  handleRefreshTest: () => Promise<void>
}

const TestContext = createContext<TestContextProps | undefined>(undefined)

export const useTestContext = () => {
  const context = useContext(TestContext)
  if (!context) {
    throw new Error('useTestContext must be used within a TestProvider')
  }
  return context
}

interface TestProviderProps {
  children: ReactNode
  id: string
  onClose: () => void
}

export const TestProvider: React.FC<TestProviderProps> = ({children, id, onClose}) => {
  const [personnels, setPersonnels] = useState<any[]>([])
  const [tests, setTests] = useState([] as any[])
  const {currentUser} = useAuth()
  const [fileData, setFileData] = useState<File[]>([])
  const [newOwner, setNewOwner] = useState('')
  const [isDropdownVisible, setDropdownVisible] = useState(false)
  const [showUploadFile, setShowUploadFile] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [policies, setPolicies] = useState([] as any[])
  const [selectedTest, setSelectedTest] = useState([] as any[])
  const [noteInput, setNoteInput] = useState('')
  const [tasks, setTasks] = useState<any[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [updatePolicy, setUpdatePolicy] = useState<any[]>([])
  const [isTestLoading, setIsTestLoading] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [editingNoteIndex, setEditingNoteIndex] = useState<number | null>(null)
  const [editedNoteContent, setEditedNoteContent] = useState('')
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])
  const [timeframe, setTimeframe] = useState('')
  const [noteSaving, setNoteSaving] = useState(false)
  const [isFileDeleteModalOpen, setIsFileDeleteModalOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<any>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isNoteDeleteModalOpen, setIsNoteDeleteModalOpen] = useState(false)
  const [selectedNoteIndex, setSelectedNoteIndex] = useState<number | null>(null)
  const [policyID, setPolicyID] = useState('')
  const { currentAuditContext } = useAuditContext()
  const [audits, setAudits] = useState<any[]>([])

  const memoizedAudits = useMemo(() => audits, [audits])

  const [isFlagModalOpen, setIsFlagModalOpen] = useState(false)
  const [isFlagging, setIsFlagging] = useState(false)

  const [isMarkAsNotRequiredModalOpen, setIsMarkAsNotRequiredModalOpen] = useState(false)
  const [isMarkingAsNotRequired, setIsMarkingAsNotRequired] = useState(false)
  const [isMarkAsResolvedModalOpen, setIsMarkAsResolvedModalOpen] = useState(false)
  const [isMarkingAsResolved, setIsMarkingAsResolved] = useState(false)
  const [isTaskRemoveModalOpen, setIsTaskRemoveModalOpen] = useState(false)
  const [taskToRemove, setTaskToRemove] = useState<string | null>(null)
  const [isRemovingTask, setIsRemovingTask] = useState(false)
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<any>(null)
  const [isRefreshingTest, setIsRefreshingTest] = useState(false)

  useEffect(() => {
    getActiveFrameworks()
  }, [])

  useEffect(() => {
    if (id) {
      setActiveTab('overview')
    }
  }, [id])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'employees')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      const risksData = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          end_date: doc.data().end_date,
        }))
        .filter((data) => !data.end_date)
      setPersonnels(risksData)
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    setIsTestLoading(true)
    setTasks([])
    getActivities()
  }, [id])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'audits')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      let Data = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          status: doc.data()?.status,
          createdAt: doc.data().createdAt,
          auditor: doc.data().auditor,
        }))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .filter((audit) => audit.status === 'Ongoing')

      if (currentUser?.role === 'Auditor') {
        Data = Data.filter((audit) => {
          if (!audit.auditor) return false
          
          if (Array.isArray(audit.auditor)) {
            return audit.auditor.some(auditor => 
              auditor.id === currentUser?.uid || auditor.email === currentUser?.email
            )
          } else {
            return audit.auditor.id === currentUser?.uid || audit.auditor.email === currentUser?.email
          }
        })
        setAudits(Data)
      }

      if (currentAuditContext){
        Data = Data.filter((audit) => audit.id === currentAuditContext.auditId)
      }
      setAudits(Data)
    })
    return unsubscribe
  }, [currentAuditContext,currentUser])

  const getActivities = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const controlsCollectionRef = collection(tenantRef, 'tasklists')
      var activeTaskListsQuery = query(controlsCollectionRef, where('test_id', '==', id))

      onSnapshot(activeTaskListsQuery, (snapshot) => {
        const controlsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }))
        setTasks(controlsData)
      })
      setIsTestLoading(false)
    } catch (error) {
      setIsTestLoading(false)
      console.error('Error getting controls:', error)
    }
  }

  const getActiveFrameworks = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)

      const testsCollectionRef = collection(tenantRef, 'active_frameworks')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const frameworksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          frameworks: doc.data().frameworks,
        }))
        const frameworks = frameworksData[0]?.frameworks || []
        setActiveFrameworks(frameworks)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting tests:', error)
    }
  }

  useEffect(() => {
    const updateDocument = async () => {
      if (!currentUser || !id || updatePolicy.length === 0) return

      const tenantId = currentUser?.tenantId || ''
      try {
        const testDocRef = doc(db, `tenants/${tenantId}/tests/${id}`)
        const testDocSnapshot = await getDoc(testDocRef)

        if (testDocSnapshot.exists()) {
          const currentData = testDocSnapshot.data()
          if (currentData.status === 'Deactivated') {
            setUpdatePolicy([])
            return
          }
          const newStatus = updatePolicy[0].status === 'Approved' ? 'Pass' : 'Fail'
          await updateDoc(testDocRef, {
            status: newStatus,
          })
          setUpdatePolicy([])
        }
      } catch (error) {
        console.error('Error updating document status: ', error)
      }
    }

    updateDocument()
  }, [currentUser, id, updatePolicy])

  useEffect(() => {
    getTests()
    getPolicy()
  }, [id])

  useEffect(() => {
    if (selectedTest && selectedTest.length > 0 && selectedTest[0]) {
      setShowUploadFile(!selectedTest[0].files || selectedTest[0].files.length === 0)
    } else {
      setShowUploadFile(false)
    }
  }, [selectedTest])

  const getPolicy = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) {
        return
      }
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'policies')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const policyData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          policy: doc.data().policy,
          status: doc.data().status,
        }))
        setPolicies(policyData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  const getTests = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)

      const testsCollectionRef = collection(tenantRef, 'tests')
      if (!id) {
        return
      }

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const testsData = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
            policies: doc.data().policies,
            policyId: doc.data().policyId,
            name: doc.data().name,
          }))
          .filter((test) => test.id === id)
        setTests(testsData)
        setPolicyID(
          testsData.length > 0 && testsData[0]?.policyId && Array.isArray(testsData[0]?.policyId)
            ? testsData[0]?.policyId[0]
            : ''
        )
        const filteredEvidences = testsData.filter((evidence) => evidence.id === id)
        if (filteredEvidences?.length > 0) {
          const selectedEvidence = filteredEvidences[0]

          if (
            selectedEvidence?.policyId &&
            Array.isArray(selectedEvidence.policyId) &&
            selectedEvidence.policyId.length > 0
          ) {
            const targetPolicyId = selectedEvidence.policyId[0]
            const updatedPolicy = policies?.filter((doc) => doc?.id === targetPolicyId) || []
            setUpdatePolicy(updatedPolicy)
          } else {
            setUpdatePolicy([])
          }
        } else {
          setUpdatePolicy([])
        }
        setSelectedTest(filteredEvidences || [])
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  const handleFileUpload = (files: File[] | null) => {
    if (files && files.length > 0) {
      setFileData((prevFiles) => [...prevFiles, ...files])
    }
  }

  const handleOwner = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOwner = event.target.value
    setNewOwner(selectedOwner)
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!id) {
        return
      }
      const testDocRef = doc(db, `tenants/${tenantId}/tests/${id}`)
      
      const selectedPersonnel = personnels.find(person => person.full_name === selectedOwner)
      const ownerUid = selectedPersonnel?.uid || selectedPersonnel?.id || ""
      
      await updateDoc(testDocRef, {
        owner: selectedOwner,
        owner_uid: ownerUid,
      })
      setNewOwner('')
    } catch (error) {
      console.error('Error assigning owner: ', error)
    }
  }

  const handleTimeframe = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedTimeframe = event.target.value
    setTimeframe(selectedTimeframe)

    const today = new Date()
    let nextReviewDate

    switch (selectedTimeframe) {
      case 'Annually':
        nextReviewDate = new Date(today.setFullYear(today.getFullYear() + 1))
        break
      case 'Bi-annually':
        nextReviewDate = new Date(today.setMonth(today.getMonth() + 6))
        break
      case 'Quarterly':
        nextReviewDate = new Date(today.setMonth(today.getMonth() + 3))
        break
      case 'Monthly':
        nextReviewDate = new Date(today.setMonth(today.getMonth() + 1))
        break
      case 'Weekly':
        nextReviewDate = new Date(today.setDate(today.getDate() + 7))
        break
      case 'Never':
        nextReviewDate = null
        break
      default:
        nextReviewDate = null
    }

    try {
      const tenantId = currentUser?.tenantId || ''
      const testDocRef = doc(db, `tenants/${tenantId}/tests/${id}`)
      await updateDoc(testDocRef, {
        timeframe: selectedTimeframe,
        next_review_date: nextReviewDate ? nextReviewDate.toISOString() : null,
      })
      setTimeframe('')
    } catch (error) {
      console.error('Error updating timeframe and next review date: ', error)
    }
  }

  const setReminder = async (taskId: string, status: boolean) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const testDocRef = doc(db, `tenants/${tenantId}/tasklists/${taskId}`)
      await updateDoc(testDocRef, {
        reminder: status,
      })
      showSuccessToast({message: 'Reminder sent successfully'})
    } catch (error) {
      console.error('Error setting reminder: ', error)
    }
  }

  const toggleDropdown = () => {
    setDropdownVisible(!isDropdownVisible)
  }

  const handleEditNote = (index: number, currentContent: string) => {
    setEditingNoteIndex(index)
    setEditedNoteContent(currentContent)
  }

  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1
  const day = currentDate.getDate()

  const todaysDate = month + '/' + day + '/' + year

  const handleSaveEditedNote = async () => {
    try {
      setNoteSaving(true)
      const tenantId = currentUser?.tenantId || ''
      const testDocRef = doc(db, `tenants/${tenantId}/tests/${id}`)

      const docSnap = await getDoc(testDocRef)
      const currentNotes = docSnap.data()?.notes || []

      const updatedNotes = currentNotes.map((note, index) =>
        index === editingNoteIndex ? {...note, note: editedNoteContent} : note
      )

      await updateDoc(testDocRef, {
        notes: updatedNotes,
      })

      setEditingNoteIndex(null)
      setEditedNoteContent('')
      setNoteSaving(false)
    } catch (error) {
      console.error('Error editing note: ', error)
    }
  }

  const handleViewClick = (file: any) => {
    setSelectedDocument(file)
    setIsDocumentViewerOpen(true)
    setDropdownVisible(false)
  }

  const handleDeleteClick = (file: any) => {
    setSelectedFile(file)
    setIsFileDeleteModalOpen(true)
  }

  const handleConfirmFileDelete = async () => {
    try {
      setIsDeleting(true)
      const tenantId = currentUser?.tenantId || ''
      const testDocRef = doc(db, `tenants/${tenantId}/tests/${id}`)

      const docSnapshot = await getDoc(testDocRef)

      if (docSnapshot.exists()) {
        const data = docSnapshot.data()
        const updatedFiles = data.files.filter((f) => f.name !== selectedFile.name)
        try {
          await deleteObject(ref(storage, selectedFile.url))
        } catch (error) {
          if (
            !(
              typeof error === 'object' &&
              error !== null &&
              'code' in error &&
              (error as any).code === 'storage/object-not-found'
            )
          ) {
            //console.error(error)
          }
        }
        if (updatedFiles.length > 0) {
          const now = new Date()
          const hasValidFile = updatedFiles.some((f) => {
            const fileDate = new Date(f.date)
            return fileDate > now
          })
          const newStatus = hasValidFile ? 'Pass' : 'Fail'
          await updateDoc(testDocRef, {files: updatedFiles, status: newStatus})
        } else {
          await updateDoc(testDocRef, {files: updatedFiles, status: 'Fail'})
        }
      }
      showSuccessToast({message: `"${selectedFile?.name}" has been deleted.`})
    } catch (error) {
      showErrorToast({message: 'An error occurred while deleting the file.'})
    } finally {
      setIsDeleting(false)
      setIsFileDeleteModalOpen(false)
      setSelectedFile(null)
      setDropdownVisible(false)
    }
  }

  const handleAddFileClick = () => {
    setShowUploadFile(true)
  }

  const handleTabClick = (tab: string) => {
    setActiveTab(tab)
  }

  const handleNotes = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!noteInput.trim()) {
      return
    }

    const currentDate = new Date()

    const day = currentDate.getDate()
    const month = currentDate.toLocaleString('default', {month: 'long'})
    const year = currentDate.getFullYear()
    const formattedDate = `${day} ${month}, ${year}`

    let hours = currentDate.getHours()
    let minutes = currentDate.getMinutes()
    let ampm = hours >= 12 ? 'PM' : 'AM'

    // Round minutes to nearest 12
    minutes = Math.round(minutes / 12) * 12
    if (minutes === 60) {
      minutes = 0
      hours += 1
    }

    hours = hours % 12
    hours = hours ? hours : 12

    const formattedTime = `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`

    try {
      const tenantId = currentUser?.tenantId || ''
      const testDocRef = doc(db, `tenants/${tenantId}/tests/${id}`)

      const docSnap = await getDoc(testDocRef)
      const currentNotes = docSnap.data()?.notes || []

      const assignedAuditId = currentAuditContext?.auditId || (currentUser?.role === 'Auditor' && audits.length > 0 ? audits[0]?.id : '')
      
      const newNoteObject = {
        addedUser: currentUser?.userName || 'Unknown User',
        addedUserUid: currentUser?.uid || '',
        addedDate: formattedDate,
        addedTime: formattedTime,
        note: noteInput.trim(),
        auditId: assignedAuditId,
      }

      const updatedNotes = [newNoteObject, ...currentNotes]
      setIsAdding(true)
      await updateDoc(testDocRef, {
        notes: updatedNotes,
      })
      setIsAdding(false)
      setNoteInput('')
    } catch (error) {
      setIsAdding(false)
    }
  }

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  function bytesToKB(bytes: number) {
    return (bytes / 1024).toFixed(2)
  }

  const handleRemoveTask = (taskId: string) => {
    setTaskToRemove(taskId)
    setIsTaskRemoveModalOpen(true)
  }

  const handleConfirmTaskRemove = async () => {
    if (!taskToRemove) return
    
    try {
      setIsRemovingTask(true)
      const tenantId = currentUser?.tenantId || ''
      const taskRef = doc(db, 'tenants', tenantId, 'tasklists', taskToRemove)
      await deleteDoc(taskRef)
      getActivities()
      showSuccessToast({message: 'Task removed successfully'})
      setIsTaskRemoveModalOpen(false)
      setTaskToRemove(null)
    } catch (error) {
      showErrorToast({message: 'An error occurred while removing the selected items.'})
    } finally {
      setIsRemovingTask(false)
    }
  }

  const handleMarkAsComplete = async (taskId: string, taskName: string) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const taskRef = doc(db, 'tenants', tenantId, 'tasklists', taskId)
      await updateDoc(taskRef, {status: 'Completed'})
      getActivities()

      const updatedTasks = tasks.filter((task) => task.id !== taskId)
      setTasks(updatedTasks)

      const tenantRef = doc(db, 'tenants', currentUser?.tenantId!)
      const activitiesRef = collection(tenantRef, 'activities')
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} has completed the task '${taskName}'`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Control added',
        target: 'Controls',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })
      showSuccessToast({message: 'Task has been marked as completed!'})
    } catch (error) {
      console.error('Error updating task status:', error)
    }
  }

  const handleMarkAsIncomplete = async (taskId: string, taskName: string) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const taskRef = doc(db, 'tenants', tenantId, 'tasklists', taskId)
      await updateDoc(taskRef, {status: 'Incomplete'})
      getActivities()

      const tenantRef = doc(db, 'tenants', currentUser?.tenantId!)
      const activitiesRef = collection(tenantRef, 'activities')
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} has marked the task '${taskName}' as Incomplete `,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Control added',
        target: 'Controls',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })
      showErrorToast({message: 'Task has been marked as incomplete!'})
    } catch (error) {
      console.error('Error updating task status:', error)
    }
  }

  const handleDeleteNote = (index: number) => {
    setSelectedNoteIndex(index)
    setIsNoteDeleteModalOpen(true)
  }

  const handleConfirmNoteDelete = async () => {
    try {
      setIsDeleting(true)
      if (selectedNoteIndex === null) return

      const tenantId = currentUser?.tenantId || ''
      const testDocRef = doc(db, `tenants/${tenantId}/tests/${id}`)

      const docSnap = await getDoc(testDocRef)
      const currentNotes = docSnap.data()?.notes || []
      const updatedNotes = currentNotes.filter((_, i) => i !== selectedNoteIndex)

      await updateDoc(testDocRef, {
        notes: updatedNotes,
      })
    } catch (error) {
      showErrorToast({message: 'An error occurred while deleting the note.'})
    } finally {
      setIsDeleting(false)
      setIsNoteDeleteModalOpen(false)
      setSelectedNoteIndex(null)
    }
  }

  const formatFileDate = (dateString: string): string => {
    if (dateString) {
      try {
        const date = parseISO(dateString)

        return format(date, 'd MMMM, yyyy')
      } catch (error) {
        console.error('Error parsing date:', error)
        return 'Invalid Date'
      }
    } else {
      return 'Invalid Date'
    }
  }

  const handleDownloadClick2 = async (customfileUrl: any) => {
    try {
      const fileUrl = customfileUrl.url

      const response = await fetch(fileUrl)
      const fileContent = await response.blob()

      const blobUrl = URL.createObjectURL(fileContent)

      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `${customfileUrl.name}`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)

      setDropdownVisible(false)
    } catch (error) {
      console.error('Error downloading policy file:', error)
    }
  }

  const handleOpenFlagModal = () => {
    setIsFlagModalOpen(true)
  }

  const handleCloseFlagModal = () => {
    setIsFlagModalOpen(false)
  }

  const handleConfirmFlag = async (reason: string) => {
    if (!reason.trim()) {
      showErrorToast({message: 'Please provide a reason for flagging.'})
      return
    }

    try {
      setIsFlagging(true)
      const tenantId = currentUser?.tenantId || ''
      const testDocRef = doc(db, `tenants/${tenantId}/tests/${id}`)

      const docSnap = await getDoc(testDocRef)
      const currentNotes = docSnap.data()?.notes || []

      const currentDate = new Date()
      const day = currentDate.getDate()
      const month = currentDate.toLocaleString('default', {month: 'long'})
      const year = currentDate.getFullYear()
      const formattedDate = `${day} ${month}, ${year}`

      let hours = currentDate.getHours()
      let minutes = currentDate.getMinutes()
      let ampm = hours >= 12 ? 'PM' : 'AM'

      minutes = Math.round(minutes / 12) * 12
      if (minutes === 60) {
        minutes = 0
        hours += 1
      }

      hours = hours % 12
      hours = hours ? hours : 12

      const formattedTime = `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`

      const newNoteObject = {
        addedUser: currentUser?.userName || 'Unknown User',
        addedUserUid: currentUser?.uid || '',
        addedDate: formattedDate,
        addedTime: formattedTime,
        auditId: audits.length > 0 ? audits[0]?.id : '',
        note: `🚩 FLAGGED: ${reason.trim()}`,
      }

      const updatedNotes = [newNoteObject, ...currentNotes]

      await updateDoc(testDocRef, {
        [`auditor_review.${audits[0]?.id}`]: {
          status: 'Flagged',
          audit_id: audits[0]?.id,
          auditor:[{
            id: currentUser?.uid,
            email: currentUser?.email,
            fullName: currentUser?.userName,
          }],
          flagged_at: new Date(),
          reason: reason.trim(),
        },
        notes: updatedNotes,
      })

      getTests()
      setIsFlagModalOpen(false)
      showSuccessToast({message: 'Test flagged successfully!'})
    } catch (error) {
      showErrorToast({message: 'An error occurred while flagging the test.'})
    } finally {
      setIsFlagging(false)
    }
  }

  const handleAccept = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const testDocRef = doc(db, `tenants/${tenantId}/tests/${id}`)
      await updateDoc(testDocRef, {
        [`auditor_review.${audits[0]?.id}`]: {
          status: 'Approved',
          audit_id: audits[0]?.id,
          auditor:[{
            id: currentUser?.uid,
            email: currentUser?.email,
            fullName: currentUser?.userName,
          }],
          approved_at: new Date(),
        }
      });
      getTests()
      showSuccessToast({message: 'Test accepted successfully!'})
    } catch (error) {
      showErrorToast({message: 'An error occurred while accepting the test.'})
    }
  }

  const handleOpenMarkAsResolvedModal = () => {
    setIsMarkAsResolvedModalOpen(true)
  }

  const handleCloseMarkAsResolvedModal = () => {
    setIsMarkAsResolvedModalOpen(false)
  }

  const handleConfirmMarkAsResolved = async () => {
    try {
      setIsMarkingAsResolved(true)
      const tenantId = currentUser?.tenantId || ''
      const testDocRef = doc(db, `tenants/${tenantId}/tests/${id}`)
      
      const docSnap = await getDoc(testDocRef)
      const currentNotes = docSnap.data()?.notes || []

      const currentDate = new Date()
      const day = currentDate.getDate()
      const month = currentDate.toLocaleString('default', {month: 'long'})
      const year = currentDate.getFullYear()
      const formattedDate = `${day} ${month}, ${year}`

      let hours = currentDate.getHours()
      let minutes = currentDate.getMinutes()
      let ampm = hours >= 12 ? 'PM' : 'AM'

      minutes = Math.round(minutes / 12) * 12
      if (minutes === 60) {
        minutes = 0
        hours += 1
      }

      hours = hours % 12
      hours = hours ? hours : 12

      const formattedTime = `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`

      const newNoteObject = {
        addedUser: currentUser?.userName || 'Unknown User',
        addedUserUid: currentUser?.uid || '',
        addedDate: formattedDate,
        addedTime: formattedTime,
        auditId: audits.length > 0 ? audits[0]?.id : '',
        note: `✅ MARKED AS RESOLVED: Flagged evidence has been resolved.`,
      }

      const updatedNotes = [newNoteObject, ...currentNotes]

      await updateDoc(testDocRef, {
        [`auditor_review.${audits[0]?.id}.status`]: 'Pass',
        notes: updatedNotes,
      })
      
      getTests()
      setIsMarkAsResolvedModalOpen(false)
      showSuccessToast({message: 'Test marked as resolved successfully!'})
    } catch (error) {
      console.error('Error marking test as resolved:', error)
      showErrorToast({message: 'An error occurred while marking the test as resolved.'})
    } finally {
      setIsMarkingAsResolved(false)
    }
  }

  const handleReEnableEvidence = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const testDocRef = doc(db, `tenants/${tenantId}/tests/${id}`)

      await updateDoc(testDocRef, {
        auditor_review: null
      })
      
      getTests()
      showSuccessToast({message: 'Evidence re-enabled successfully!'})
    } catch (error) {
      console.error('Error re-enabling evidence:', error)
      showErrorToast({message: 'An error occurred while re-enabling the evidence.'})
    }
  }

  const handleOpenMarkAsNotRequiredModal = () => {
    setIsMarkAsNotRequiredModalOpen(true)
  }

  const handleCloseMarkAsNotRequiredModal = () => {
    setIsMarkAsNotRequiredModalOpen(false)
  }

  const handleConfirmMarkAsNotRequired = async (reason: string) => {
    if (!reason.trim()) {
      showErrorToast({message: 'Please provide a reason for marking as not applicable.'})
      return
    }

    try {
      setIsMarkingAsNotRequired(true)
      const tenantId = currentUser?.tenantId || ''
      const testDocRef = doc(db, `tenants/${tenantId}/tests/${id}`)

      const docSnap = await getDoc(testDocRef)
      const currentNotes = docSnap.data()?.notes || []

      const currentDate = new Date()
      const day = currentDate.getDate()
      const month = currentDate.toLocaleString('default', {month: 'long'})
      const year = currentDate.getFullYear()
      const formattedDate = `${day} ${month}, ${year}`

      let hours = currentDate.getHours()
      let minutes = currentDate.getMinutes()
      let ampm = hours >= 12 ? 'PM' : 'AM'

      minutes = Math.round(minutes / 12) * 12
      if (minutes === 60) {
        minutes = 0
        hours += 1
      }

      hours = hours % 12
      hours = hours ? hours : 12

      const formattedTime = `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`

      const newNoteObject = {
        addedUser: currentUser?.userName || 'Unknown User',
        addedUserUid: currentUser?.uid || '',
        addedDate: formattedDate,
        addedTime: formattedTime,
        auditId: audits.length > 0 ? audits[0]?.id : '',
        note: `🚫 MARKED AS NOT REQUIRED: ${reason.trim()}`,
      }

      const updatedNotes = [newNoteObject, ...currentNotes]

      await updateDoc(testDocRef, {
        [`auditor_review.${audits[0]?.id}`]: {
          status: 'Not applicable',
          audit_id: audits[0]?.id,
          auditor:[{
            id: currentUser?.uid,
            email: currentUser?.email,
            fullName: currentUser?.userName,
          }],
          marked_at: new Date(),
          reason: reason.trim(),
        },
        notes: updatedNotes,
      })

      getTests()
      setIsMarkAsNotRequiredModalOpen(false)
      showSuccessToast({message: 'Test marked as not required successfully!'})
    } catch (error) {
      console.error('Error marking test as not required:', error)
      showErrorToast({message: 'An error occurred while marking the test as not required.'})
    } finally {
      setIsMarkingAsNotRequired(false)
    }
  }

  const handleRefreshTest = async () => {
    if (!selectedTest.length || !currentUser?.tenantId) {
      showErrorToast({message: 'No test selected or user not authenticated'})
      return
    }

    const test = selectedTest[0]
    if (!test.automated_type || !test.integration) {
      showErrorToast({message: 'This test cannot be refreshed automatically'})
      return
    }

    setIsRefreshingTest(true)
    try {
      const tenantRef = doc(db, 'tenants', currentUser.tenantId)
      const integrationsRef = collection(tenantRef, 'integrations')
      const integrationQuery = query(integrationsRef, where('name', '==', test.integration))
      const integrationSnapshot = await getDocs(integrationQuery)
      
      if (integrationSnapshot.empty) {
        showErrorToast({message: 'Integration data not found'})
        return
      }

      const integrationData = integrationSnapshot.docs[0].data()
      
      const evaluationResult = await evaluateTest(
        test.test_id,
        test.integration,
        integrationData,
        currentUser.tenantId
      )

      const testRef = doc(db, 'tenants', currentUser.tenantId, 'tests', test.test_id)
      await updateDoc(testRef, {
        status: evaluationResult.status,
        last_evaluated: serverTimestamp(),
        evaluation_score: evaluationResult.score,
        evaluation_details: evaluationResult.details,
        conditions_met: evaluationResult.conditions_met,
        conditions_failed: evaluationResult.conditions_failed,
        updated_at: serverTimestamp(),
      })

      await getTests()
      showSuccessToast({message: 'Test refreshed successfully!'})
    } catch (error) {
      console.error('Error refreshing test:', error)
      showErrorToast({message: 'Failed to refresh test. Please try again.'})
    } finally {
      setIsRefreshingTest(false)
    }
  }

  const value = {
    // Test data
    tests,
    selectedTest,
    policies,
    updatePolicy,
    personnels,
    activeFrameworks,
    policyID,

    // File handling
    fileData,
    showUploadFile,
    isDropdownVisible,
    selectedFile,
    isFileDeleteModalOpen,
    isDeleting,

    // Notes
    noteInput,
    editingNoteIndex,
    editedNoteContent,
    isNoteDeleteModalOpen,
    selectedNoteIndex,
    noteSaving,
    isAdding,

    // Flag modal
    isFlagModalOpen,
    isFlagging,

    // Mark as not required modal
    isMarkAsNotRequiredModalOpen,
    isMarkingAsNotRequired,

    // Mark as resolved modal
    isMarkAsResolvedModalOpen,
    isMarkingAsResolved,

    // Tasks
    tasks,
    isModalOpen,
    isTestLoading,

    // UI state
    activeTab,
    newOwner,
    timeframe,

    // Current user and ID
    currentUser,
    id,

    // Functions
    getTests,
    getPolicy,
    getActivities,
    getActiveFrameworks,

    // File handlers
    handleFileUpload,
    handleViewClick,
    handleDeleteClick,
    handleConfirmFileDelete,
    handleAddFileClick,
    handleDownloadClick2,
    toggleDropdown,

    // Note handlers
    handleNotes,
    setNoteInput,
    handleEditNote,
    handleSaveEditedNote,
    handleDeleteNote,
    handleConfirmNoteDelete,
    setEditingNoteIndex,
    setEditedNoteContent,
    setIsFileDeleteModalOpen,
    setIsNoteDeleteModalOpen,

    // Flag handlers
    handleOpenFlagModal,
    handleCloseFlagModal,
    handleConfirmFlag,
    handleAccept,
    handleReEnableEvidence,

    // Mark as not required handlers
    handleOpenMarkAsNotRequiredModal,
    handleCloseMarkAsNotRequiredModal,
    handleConfirmMarkAsNotRequired,

    // Mark as resolved handlers
    handleOpenMarkAsResolvedModal,
    handleCloseMarkAsResolvedModal,
    handleConfirmMarkAsResolved,

    // Task handlers
    handleOpenModal,
    handleCloseModal,
    handleRemoveTask,
    handleConfirmTaskRemove,
    handleMarkAsComplete,
    handleMarkAsIncomplete,
    setReminder,

    // Task removal modal state
    isTaskRemoveModalOpen,
    isRemovingTask,
    setIsTaskRemoveModalOpen,

    // Other handlers
    handleOwner,
    handleTimeframe,
    handleTabClick,

    // Utility functions
    bytesToKB,
    formatFileDate,
    getReviewStatus,

    // Audit context
    currentAuditContext,

    // Audits
    audits: memoizedAudits,

    // Document viewer modal
    isDocumentViewerOpen,
    selectedDocument,
    setIsDocumentViewerOpen,
    setSelectedDocument,

    // Test refresh
    isRefreshingTest,
    handleRefreshTest,
  }

  return (
    <TestContext.Provider value={value}>
      {children}
      <ConfirmationModal
        isModalOpen={isTaskRemoveModalOpen}
        hideModal={() => setIsTaskRemoveModalOpen(false)}
        confirmBtnClickHandler={handleConfirmTaskRemove}
        title="Remove Selected Items"
        message="Are you sure you want to remove the selected items? This action cannot be undone."
        confirmButtonText="Remove"
        type="delete"
        isLoading={isRemovingTask}
      />
    </TestContext.Provider>
  )
}
